import type { IconName } from '@tutero/ui';

import type { PlatformRole } from '@/lib/accounts/account-platform';
import {
  QuickSearchParentsDocument,
  QuickSearchStudentsDocument,
  QuickSearchTutorsDocument,
} from '@/lib/generated/graphql';
import { requestLive } from '@/lib/graph';
import type { Permission } from '@/shell/tool';

/**
 * Quick Search: one box in the sidebar, and ⌘K / Ctrl+K, over every tutor, parent and student,
 * opening the one picked on its own tool's detail page, or its platform as them (the row's own
 * button, or ⌘↵ / Ctrl+Enter): a few hits of each kind, under a heading
 * of the kind, searched together as the words settle — the design of a Flutter prototype of the
 * same search, which never shipped.
 *
 * No search endpoint spans the kinds, so each kind is its list's own read narrowed by the
 * service's `search`. Every read goes to live tutoring, as the details it opens do
 * (`src/lib/accounts/graph.ts`).
 */

/** How many hits a kind shows: enough to pick from, few enough that every kind fits. */
export const HITS_PER_KIND = 5;

export interface QuickSearchHit {
  readonly id: string;
  readonly name: string;
  /** A second word that tells two of the same name apart: a region, the children, a year. */
  readonly hint: string | null;
}

export interface QuickSearchKind {
  /** The tool a hit opens in, at `/<tool>/<id>`. */
  readonly tool: 'tutors' | 'parents' | 'students';
  /** Whose platform a hit opens, which the platform's URL needs (`account-platform.ts`). */
  readonly role: PlatformRole;
  readonly title: string;
  /** The tool's own grant: a kind the staff member cannot open is not searched (A4). */
  readonly permission: Permission;
  /** The tool's own nav glyph, so a hit reads as the row it opens. */
  readonly icon: IconName;
  readonly search: (text: string) => Promise<readonly QuickSearchHit[]>;
}

/** What the search sends: the words, and the handful it asks for. */
const asked = (text: string) => ({ search: text, limit: HITS_PER_KIND });

export const KINDS: readonly QuickSearchKind[] = [
  {
    tool: 'tutors',
    role: 'tutor',
    title: 'Tutors',
    permission: 'tutors:use',
    icon: 'group',
    search: async (text) => {
      const data = await requestLive(QuickSearchTutorsDocument, asked(text));
      return data.tutors.edges.map(({ node }) => ({
        id: node.id,
        name: node.name,
        hint: node.region?.title ?? null,
      }));
    },
  },
  {
    tool: 'parents',
    role: 'parent',
    title: 'Parents',
    permission: 'parents:use',
    icon: 'person',
    search: async (text) => {
      const data = await requestLive(QuickSearchParentsDocument, asked(text));
      return data.parents.edges.map(({ node }) => ({
        id: node.id,
        name: node.name,
        hint: node.children.map((child) => child.name).join(', ') || null,
      }));
    },
  },
  {
    tool: 'students',
    role: 'student',
    title: 'Students',
    permission: 'students:use',
    icon: 'mood',
    search: async (text) => {
      const data = await requestLive(QuickSearchStudentsDocument, asked(text));
      return data.students.edges.map(({ node }) => ({
        id: node.id,
        name: node.name,
        hint: node.year?.title ?? null,
      }));
    },
  },
];

/** The kinds a staff member's grants open. */
export function kindsFor(perms: readonly string[]): readonly QuickSearchKind[] {
  return KINDS.filter((kind) => perms.includes(kind.permission));
}
